import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { coffeeItems } from '../data/coffeeData';
import './SearchModal.css';

const SearchModal = ({ onClose, onSelectCoffee }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredItems, setFilteredItems] = useState(coffeeItems);
  const modalRef = useRef(null);
  const contentRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Entrance animation
    gsap.fromTo(
      modalRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 0.3, ease: 'power2.inOut' }
    );

    gsap.fromTo(
      contentRef.current,
      { 
        scale: 0.9,
        y: 40,
        opacity: 0
      },
      { 
        scale: 1,
        y: 0,
        opacity: 1,
        duration: 0.5,
        ease: 'back.out(1.4)',
        delay: 0.1
      }
    );

    // Focus input and show keyboard
    setTimeout(() => {
      inputRef.current?.focus();
    }, 500);
  }, []);

  useEffect(() => {
    // Filter items based on search query
    if (searchQuery.trim() === '') {
      setFilteredItems(coffeeItems);
    } else {
      const filtered = coffeeItems.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredItems(filtered);
    }
  }, [searchQuery]);

  const handleClose = () => {
    gsap.to(contentRef.current, {
      scale: 0.9,
      y: 30,
      opacity: 0,
      duration: 0.3,
      ease: 'back.in(1.2)'
    });
    gsap.to(modalRef.current, {
      opacity: 0,
      duration: 0.25,
      delay: 0.05,
      ease: 'power2.inOut',
      onComplete: onClose
    });
  };

  const handleItemClick = (item) => {
    onSelectCoffee(item);
    handleClose();
  };

  return (
    <div className="search-modal-overlay" ref={modalRef} onClick={handleClose}>
      <div className="search-modal-content" ref={contentRef} onClick={(e) => e.stopPropagation()}>
        <button className="search-close-btn" onClick={handleClose}>✕</button>
        
        <div className="search-header">
          <div className="search-input-container">
            <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              ref={inputRef}
              type="text"
              className="search-input"
              placeholder="Search for coffee..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
            />
            {searchQuery && (
              <button 
                className="clear-search-btn"
                onClick={() => setSearchQuery('')}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="search-results">
          {filteredItems.length > 0 ? (
            <div className="results-list">
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  className="search-result-item"
                  onClick={() => handleItemClick(item)}
                >
                  <div className="result-image-container">
                    <img
                      src={item.imagePath}
                      alt={item.name}
                      className="result-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="result-image-placeholder" style={{ display: 'none' }}>
                      <span className="result-placeholder-icon">☕</span>
                    </div>
                  </div>
                  <div className="result-info">
                    <span className="result-type">{item.type}</span>
                    <h3 className="result-name">{item.name}</h3>
                    <p className="result-description">{item.description}</p>
                  </div>
                  <div className="result-price">{item.price}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-results">
              <svg className="no-results-icon" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
                <line x1="11" y1="8" x2="11" y2="14"></line>
                <line x1="8" y1="11" x2="14" y2="11"></line>
              </svg>
              <p className="no-results-text">No coffee found</p>
              <p className="no-results-subtitle">Try searching for something else</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
